import React, { useEffect, useState } from "react";
import styles from "./DatePicker.module.css";

/**
 * [R22, poprawka po przeglądzie 2026-09-13] MINUTY SPOZA KWADRANSA SĄ WARTOŚCIĄ, NIE BŁĘDEM DO ZAOKRĄGLENIA.
 *
 * BYŁO ŹLE: TimePicker i DateTimePicker przy WCZYTANIU wartości „przyciągały” minuty do siatki 0/15/30/45.
 * Dopóki stały w nich tylko pola, które i tak zapisywały kwadranse, nikt tego nie widział. Pakiet R22
 * przepiął na te pickery pola, które wcześniej były natywnymi `<input type="time|datetime-local">`
 * (godziny wyjazdu i powrotu delegacji — z nich liczą się diety — oraz godziny zmian w grafiku). Zapisane
 * 08:10 pokazywało się jako 08:15, a KAŻDA edycja (klik w dzień, zmiana godziny, wpis daty) oddawała :15.
 * Format napisu się zgadzał, wartość już nie.
 *
 * NAPRAWA:
 *  - wczytana wartość zostaje co do minuty (wyzwalacz, pokrętło i to, co oddaje picker),
 *  - strzałki minut idą do NASTĘPNEGO / POPRZEDNIEGO kwadransa (dla 08:00, 08:15… zachowanie jak dotąd),
 *  - pole „Wpisz godzinę” (gg:mm) daje każdą minutę — tak jak dawała natywna kontrolka,
 *  - DOMYŚLNA godzina pustego pola i przycisk „Teraz” zostają w siatce kwadransów (brief B-9c).
 */

export const KWADRANSE = [0, 15, 30, 45];

const pad2 = (n) => String(n).padStart(2, "0");

/** Następny kwadrans PO minucie (45 → 0). Minuta z siatki przechodzi na kolejną pozycję siatki. */
export function minutaWGore(minuta) {
    const nastepny = KWADRANSE.find((k) => k > minuta);
    return nastepny === undefined ? KWADRANSE[0] : nastepny;
}

/** Poprzedni kwadrans PRZED minutą (0 → 45). 08:10 w dół daje 08:00, nie 07:45. */
export function minutaWDol(minuta) {
    const poprzednie = KWADRANSE.filter((k) => k < minuta);
    return poprzednie.length === 0 ? KWADRANSE[KWADRANSE.length - 1] : poprzednie[poprzednie.length - 1];
}

/** Najbliższy kwadrans — tylko dla „Teraz” (akcja użytkownika), nigdy dla wczytanej wartości. */
export function najblizszyKwadrans(minuta) {
    return KWADRANSE.reduce((prev, curr) => (Math.abs(curr - minuta) < Math.abs(prev - minuta) ? curr : prev));
}

/**
 * Godzina z klawiatury → { hour, minute } albo null. Przyjmuje "7:50", "07:50", "07.50" i same cyfry "0750".
 * Godzina 0–23, minuty 0–59 i ZAWSZE dwie cyfry minut — "7:5" czeka na dalszy wpis.
 */
export function parsujGodzineZKlawiatury(tekst) {
    if (typeof tekst !== "string") return null;
    const t = tekst.trim();
    const dop = t.match(/^(\d{1,2})[:.](\d{2})$/) || t.match(/^(\d{2})(\d{2})$/);
    if (!dop) return null;
    const hour = Number(dop[1]);
    const minute = Number(dop[2]);
    if (hour > 23 || minute > 59) return null;
    return { hour, minute };
}

/**
 * Pole „Wpisz godzinę” pod pokrętłami godzin i minut.
 *
 * @param {number}   hour / minute        godzina stojąca w pickerze
 * @param {boolean}  maWartosc            czy pole ma wartość (pusty TimePicker nie podpowiada 08:00 w polu wpisu)
 * @param {Function} onWpisanaGodzina     (hour, minute) → pełna, poprawna godzina z klawiatury
 * @param {Function} [onZatwierdz]        Enter przy poprawnej godzinie
 */
export default function WpisGodziny({ hour, minute, maWartosc = true, onWpisanaGodzina, onZatwierdz }) {
    const zStanu = maWartosc ? `${pad2(hour)}:${pad2(minute)}` : "";
    const [tekst, setTekst] = useState(zStanu);

    // Zmiana z pokrętła, siatki albo „Teraz” odświeża pole wpisu.
    useEffect(() => { setTekst(zStanu); }, [zStanu]);

    const wpis = (e) => {
        const t = e.target.value;
        setTekst(t);
        const p = parsujGodzineZKlawiatury(t);
        if (p) onWpisanaGodzina(p.hour, p.minute);
    };

    const klawisz = (e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        const p = parsujGodzineZKlawiatury(tekst);
        if (!p) return;
        onWpisanaGodzina(p.hour, p.minute);
        if (onZatwierdz) onZatwierdz();
    };

    return (
        <label className={styles.wpisDatyRow}>
            <span className={styles.wpisDatyLabel}>Wpisz godzinę</span>
            <input
                type="text"
                inputMode="numeric"
                autoComplete="off"
                className={styles.wpisDaty}
                placeholder="gg:mm"
                maxLength={5}
                value={tekst}
                onChange={wpis}
                onKeyDown={klawisz}
            />
        </label>
    );
}
